/**
 * Standard Actions configuration for Dawn.
 *
 * Storefront Renderer injects the Shopify Standard Actions bundle
 * (`window.Shopify.actions.{updateCart,openCart,getCart,…}`). This file
 * overrides the bundle's built-in Dawn refresh path with an explicit,
 * in-theme version so forks that change Dawn's cart contract keep
 * working. Remove this file and the built-in defaults take over.
 *
 *   - openCart   — opens <cart-drawer>; falls back to /cart.
 *   - updateCart — after the Storefront API mutation, refreshes the
 *     affected cart sections and publishes `cart-update` so Dawn's
 *     pubsub subscribers react.
 *   - other actions (getCart, etc.) keep the default implementation.
 */

// Cart custom elements that advertise sections via getSectionsToRender().
// If Dawn adds a new cart custom element, add its tag here.
const DAWN_CART_TAGS = ['cart-drawer', 'cart-items', 'cart-drawer-items', 'cart-notification'];

// Sections that Dawn's own pubsub subscribers refresh (cart.js's
// CartItems#onCartUpdate fetches and replaces these directly when
// cart-update fires; cart-drawer.js's renderContents handles the
// drawer body). We skip them here to avoid double-rendering.
// Format is '<element-tag>:<getSectionsToRender entry id>'.
// If you change which sections those subscribers refresh, update this set.
const DAWN_PUBSUB_REFRESHED_SECTIONS = new Set([
  'cart-drawer:cart-drawer',
  'cart-drawer-items:CartDrawer',
  'cart-items:main-cart-items',
]);

// Walk every mounted Dawn cart custom element, collect the sections it
// wants rendered, and dedupe. Returns a Map keyed by section id, each
// entry pointing at the DOM mount and the selector used to extract the
// fresh fragment.
function collectCartSections() {
  const sections = new Map();

  for (const el of document.querySelectorAll(DAWN_CART_TAGS.join(','))) {
    let entries;
    try {
      entries = el.getSectionsToRender?.();
    } catch {
      continue;
    }

    const tag = el.tagName.toLowerCase();
    for (const entry of entries ?? []) {
      if (DAWN_PUBSUB_REFRESHED_SECTIONS.has(`${tag}:${entry.id}`)) continue;

      const sectionId = entry.section ?? entry.id;
      if (!sectionId || sections.has(sectionId)) continue;

      // Two patterns coexist in getSectionsToRender():
      //   - cart-items style: entry.section is the parent Liquid section
      //     id, entry.selector is a child node inside it.
      //   - cart-drawer / cart-notification style: entry.id IS the mount;
      //     there is no parent wrapper.
      const root = entry.section ? document.getElementById(entry.id) : document;
      if (!root) continue;

      const mount = entry.selector
        ? (root.querySelector(entry.selector) ?? (entry.section ? root : null))
        : document.getElementById(entry.id);
      if (!mount) continue;

      sections.set(sectionId, {
        mount,
        extractSelector: entry.selector || '.shopify-section',
      });
    }
  }

  return sections;
}

// After a Storefront API mutation, refresh every Dawn cart section
// that isn't already refreshed by Dawn's own pubsub subscribers, then
// publish 'cart-update' so the subscribers run.
//
// We always fetch /cart.js (with sections= when we have any) so that
// `cartData` is defined for subscribers. quick-add-bulk.js reads
// `event.cartData.items` unconditionally — publishing without cartData
// makes it throw.
async function refreshDawnCartUI() {
  const sections = collectCartSections();
  const sectionsQuery = sections.size
    ? `?sections=${[...sections.keys()].join(',')}`
    : '';
  // `routes` is a Dawn global, but don't assume it's defined.
  const cartUrl = (typeof routes !== 'undefined' && routes?.cart_url) || '/cart';
  const url = `${cartUrl}.js${sectionsQuery}`;
  const cartData = await fetch(url, { headers: { Accept: 'application/json' } })
    .then((r) => (r.ok ? r.json() : null))
    .catch(() => null);

  if (cartData?.sections) {
    for (const [id, { mount, extractSelector }] of sections) {
      const html = cartData.sections[id];
      if (!html) continue;
      const source = new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector(extractSelector);
      if (source) mount.replaceChildren(...source.childNodes);
    }
  }

  // Hand off to Dawn's existing subscribers. cartData is the full
  // Cart Ajax payload (items, item_count, token, …) plus sections;
  // quick-add-bulk.js and price-per-item.js read it directly.
  publish(PUB_SUB_EVENTS.cartUpdate, {
    source: 'external-refresh',
    cartData: cartData ?? undefined,
  });
}

function initStandardActions() {
  const actions = window.Shopify?.actions;
  if (!actions) return;

  actions.openCart.configure({
    async handler(defaultHandler) {
      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.open === 'function') {
        drawer.open();
        return;
      }
      return defaultHandler();
    },
  });

  actions.updateCart.configure({
    // Dawn doesn't currently listen for shopify:cart:* events, but the
    // bundle requires an eventTarget. document is the conventional root.
    eventTarget: () => document,
    async handler(defaultHandler) {
      const result = await defaultHandler();
      try {
        await refreshDawnCartUI();
      } catch (error) {
        console.error('[Dawn] Standard Actions cart refresh failed; reloading.', error);
        window.location.reload();
      }
      return result;
    },
  });
}

// Run immediately if the standard-actions bundle has already attached
// `Shopify.actions`; otherwise wait for DOMContentLoaded, which fires after
// all module scripts have executed regardless of document order.
if (window.Shopify?.actions) {
  initStandardActions();
} else {
  document.addEventListener('DOMContentLoaded', initStandardActions, { once: true });
}

// Draft gelato products are not available through Liquid's all_products.
// Replace the temporary tea cards with their registered Shopify media
// without publishing the products or changing the live theme.
function initDraftGelatoCards() {
  const carousel = document.querySelector('.gelato-prods .carousel');
  if (!carousel) return;

  const gelatoProducts = [
    {
      title: '宇治抹茶ジェラート',
      description: '宇治抹茶の香りと味わいを、なめらかな口どけに。',
      image: 'https://cdn.shopify.com/s/files/1/0754/6024/7723/files/UJI_MATCHA.png?v=1784854117&width=720',
    },
    {
      title: '浅炒りほうじ茶ジェラート',
      description: '浅炒りほうじ茶のやわらかな香ばしさを、冷たいひとさじに。',
      image: 'https://cdn.shopify.com/s/files/1/0754/6024/7723/files/HOUJICHA.png?v=1784854096&width=720',
    },
    {
      title: '宇治抹茶山椒ジェラート',
      description: '宇治抹茶の余韻に、山椒の爽やかな香りを重ねました。',
      image: 'https://cdn.shopify.com/s/files/1/0754/6024/7723/files/MATCHA_SANSHO.png?v=1784854067&width=720',
    },
    {
      title: 'ほうじ茶シナモンジェラート',
      description: 'ほうじ茶の香ばしさに、シナモンの温かな香りを添えて。',
      image: 'https://cdn.shopify.com/s/files/1/0754/6024/7723/files/HOUJI_CINNAMON.png?v=1784854040&width=720',
    },
  ];

  const cards = [...carousel.querySelectorAll('.prod')];
  cards.forEach((card, index) => {
    const product = gelatoProducts[index];
    if (!product) {
      card.remove();
      return;
    }

    const image = card.querySelector('.ph img');
    const imageLink = card.querySelector('.prod-image-link');
    const heading = card.querySelector('h3');
    const description = card.querySelector('.ds');
    const detail = card.querySelector('.detail');

    if (image) {
      image.src = product.image;
      image.alt = product.title;
      image.width = 720;
      image.height = 720;
    }
    if (imageLink?.matches('a')) {
      const imageFrame = document.createElement('div');
      imageFrame.className = imageLink.className;
      imageFrame.append(...imageLink.childNodes);
      imageLink.replaceWith(imageFrame);
    }
    if (heading) heading.textContent = product.title;
    if (description) description.textContent = product.description;

    card.querySelector('.variant-field')?.remove();
    card.querySelector('.pr')?.remove();
    if (detail) {
      detail.classList.remove('add-to-cart');
      detail.disabled = true;
      detail.textContent = '商品ページ準備中';
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initDraftGelatoCards, { once: true });
} else {
  initDraftGelatoCards();
}

